import React, { useState } from 'react';
import { format } from 'date-fns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import AppointmentDetails from './AppointmentDetails';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Appointment, AppointmentStatus } from '../types/appointment.types';
import { appointmentApi } from '../api/appointmentApi';

interface ViewAppointmentDialogProps {
  open: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  onStatusAction?: (action: string) => void;
  onStatusUpdate?: (updatedAppointment: Appointment) => void;
  loading?: boolean;
}

// Map action strings to AppointmentStatus values
const actionToStatus: Record<string, AppointmentStatus> = {
  'arrived': AppointmentStatus.ARRIVED,
  'start': AppointmentStatus.IN_PROGRESS,
  'cancel': AppointmentStatus.CANCELLED,
  'no_show': AppointmentStatus.NO_SHOW,
  'reschedule': AppointmentStatus.RESCHEDULED,
  'complete': AppointmentStatus.COMPLETED,
  'confirm': AppointmentStatus.CONFIRMED,
};

const statusActions: Partial<Record<AppointmentStatus, Array<{ 
  label?: string; 
  action: string; 
  color?: 'primary' | 'error' | 'warning' | 'success' | 'secondary' | 'info'; 
  icon?: React.ReactNode 
}>>> = {
  [AppointmentStatus.SCHEDULED]: [
    { label: 'Mark as Arrived', action: 'arrived', color: 'info' },
    { label: 'Cancel', action: 'cancel', color: 'error' },
    { label: 'No Show', action: 'no_show', color: 'warning' },
    { label: 'Reschedule', action: 'reschedule', color: 'secondary' },
  ],
  [AppointmentStatus.CONFIRMED]: [
    { label: 'Mark as Arrived', action: 'arrived', color: 'info' },
    { label: 'Cancel', action: 'cancel', color: 'error' },
    { label: 'No Show', action: 'no_show', color: 'warning' },
    { label: 'Reschedule', action: 'reschedule', color: 'secondary' },
  ],
  [AppointmentStatus.ARRIVED]: [
    { label: 'Start Consultation', action: 'start', color: 'primary' },
    { label: 'Cancel', action: 'cancel', color: 'error' },
  ],
  [AppointmentStatus.IN_PROGRESS]: [
    { label: 'Complete', action: 'complete', color: 'success' },
    { label: 'Cancel', action: 'cancel', color: 'error' },
  ],
  [AppointmentStatus.RESCHEDULED]: [
    { label: 'Mark as Arrived', action: 'arrived', color: 'info' },
    { label: 'Cancel', action: 'cancel', color: 'error' },
    { label: 'No Show', action: 'no_show', color: 'warning' },
  ],
  [AppointmentStatus.CANCELLED]: [
    { label: 'Reschedule', action: 'reschedule', color: 'secondary' },
  ],
  [AppointmentStatus.NO_SHOW]: [
    { label: 'Reschedule', action: 'reschedule', color: 'secondary' },
  ],
  [AppointmentStatus.COMPLETED]: [
    // No actions for completed appointments
  ],
};

const ViewAppointmentDialog: React.FC<ViewAppointmentDialogProps> = ({
  open,
  appointment,
  onClose,
  onStatusAction,
  onStatusUpdate,
  loading = false,
}) => {
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<Date | null>(new Date());
  const [isRescheduling, setIsRescheduling] = useState(false);
  
  if (!appointment) return null;

  const actions = statusActions[appointment.status] || [];

  const handleStatusActionClick = async (action: string) => {
    if (action === 'reschedule') {
      setIsRescheduleOpen(true);
      return;
    }

    if (onStatusAction) {
      onStatusAction(action);
    }
    
    // If the action maps to a status update and we have an onStatusUpdate handler
    if (actionToStatus[action] && onStatusUpdate) {
      try {
        const newStatus = actionToStatus[action];
        const updatedAppointment = await appointmentApi.updateStatus(appointment.id, newStatus);
        
        // Notify parent component about the status update
        onStatusUpdate(updatedAppointment);
        
        // Show success toast
        toast.success(`Appointment marked as ${newStatus.toLowerCase().replace('_', ' ')}`, {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        
        // Close the dialog after a short delay
        setTimeout(() => {
          onClose();
        }, 1000);
        
      } catch (error) {
        console.error('Failed to update appointment status:', error);
        
        // Show error toast
        toast.error(`Failed to update appointment status: ${error instanceof Error ? error.message : 'Unknown error'}`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    }
  };
  
  const handleStatusUpdate = async (updatedAppointment: any) => {
    if (onStatusUpdate) {
      onStatusUpdate(updatedAppointment);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
<AppointmentDetails 
        appointment={appointment} 
        onStatusUpdate={handleStatusUpdate}
        onClose={onClose}
      />

      {/* Reschedule Dialog */}
      <Dialog open={isRescheduleOpen} onClose={() => !isRescheduling && setIsRescheduleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reschedule Appointment</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ mt: 2, mb: 3 }}>
              <DateTimePicker
                label="New Date & Time"
                value={rescheduleDate}
                onChange={(newValue) => setRescheduleDate(newValue)}
                minDateTime={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setIsRescheduleOpen(false)} 
            disabled={isRescheduling}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              if (!rescheduleDate) return;
              
              try {
                setIsRescheduling(true);
                const rescheduleData = {
                  date: rescheduleDate.toISOString().split('T')[0],
                  time: format(rescheduleDate, 'HH:mm'),
                  notes: `Rescheduled from ${format(new Date(appointment.date!), 'PPp')}`,
                };
                
                // Calculate duration from existing appointment times if available
                let duration = 30; // Default duration in minutes
                if (appointment.startTime && appointment.endTime) {
                  const start = new Date(`2000-01-01T${appointment.startTime}`);
                  const end = new Date(`2000-01-01T${appointment.endTime}`);
                  duration = Math.round((end.getTime() - start.getTime()) / (1000 * 60));
                }

                // Create update data with required AppointmentFormData properties
                const updateData = {
                  patientId: appointment.patient?.id || '',
                  doctorId: appointment.doctor?.id || '',
                  locationId: appointment.location?.id || '',
                  date: rescheduleData.date,
                  time: rescheduleData.time,
                  duration: duration,
                  type: appointment.type || 'CONSULTATION',
                  status: appointment.status,
                  reason: appointment.reason,
                  notes: rescheduleData.notes,
                  isWalkIn: appointment.isWalkIn || false,
                  priority: appointment.priority || 'MEDIUM',
                };
                
                // Update the appointment details
                await appointmentApi.update(appointment.id, updateData);
                
                // Update status to RESCHEDULED
                const statusUpdatedAppointment = await appointmentApi.updateStatus(
                  appointment.id, 
                  AppointmentStatus.RESCHEDULED,
                  rescheduleData.notes
                );
                
                onStatusUpdate?.(statusUpdatedAppointment);
                setIsRescheduleOpen(false);
                
                // Show success toast
                toast.success('Appointment rescheduled successfully', {
                  position: 'top-right',
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                });
                
              } catch (error) {
                console.error('Failed to reschedule appointment:', error);
                
                // Show error toast
                toast.error(`Failed to reschedule appointment: ${error instanceof Error ? error.message : 'Unknown error'}`, {
                  position: 'top-right',
                  autoClose: 5000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  draggable: true,
                });
              } finally {
                setIsRescheduling(false);
              }
            }}
            color="primary"
            variant="contained"
            disabled={isRescheduling || !rescheduleDate}
          >
            {isRescheduling ? 'Rescheduling...' : 'Confirm Reschedule'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ViewAppointmentDialog;